import { useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';
import { blogArticles } from '../../../mocks/blog';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cartadoeufuturo.com.br';

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(
    () => blogArticles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return blogArticles
      .filter((a) => a.slug !== slug && a.category === article.category)
      .slice(0, 3);
  }, [article, slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-cream-50 flex flex-col items-center justify-center gap-6">
        <div className="w-20 h-20 flex items-center justify-center bg-cream-100 rounded-full border border-gold-200">
          <i className="ri-article-line text-4xl text-gold-500"></i>
        </div>
        <h1 className="font-['Playfair_Display'] text-2xl text-charcoal-800">Artigo não encontrado</h1>
        <p className="text-charcoal-500 font-['Montserrat']">O artigo que você procura não existe ou foi removido.</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-white font-['Montserrat'] font-semibold rounded-xl hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Voltar ao Blog
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author.name,
      url: `${SITE_URL}/sobre`,
      image: article.author.avatar,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Carta do Eu Futuro',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/og-image.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${article.slug}`,
    },
    url: `${SITE_URL}/blog/${article.slug}`,
    articleSection: article.category,
    keywords: article.tags?.join(', ') || article.category,
    inLanguage: 'pt-BR',
  };

  const formattedDate = new Date(article.publishedAt).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <div className="min-h-screen bg-cream-50">
      <SEOHead
        title={`${article.title} | Blog Carta do Eu Futuro`}
        description={article.excerpt}
        keywords={article.tags?.join(', ') || article.category}
        canonical={`/blog/${article.slug}`}
        ogImage={article.coverImage}
        schema={articleSchema}
      />
      <Navbar />

      {/* Hero com imagem de capa */}
      <section className="relative pt-20">
        <div className="relative h-[480px] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/85 via-charcoal-950/40 to-charcoal-950/20 z-10"></div>
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 z-20 flex flex-col justify-end">
            <div className="max-w-4xl mx-auto px-6 pb-12 w-full">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-white/60 text-sm mb-4 font-['Montserrat']">
                <Link to="/" className="hover:text-gold-400 transition-colors">Início</Link>
                <i className="ri-arrow-right-s-line text-white/30"></i>
                <Link to="/blog" className="hover:text-gold-400 transition-colors">Blog</Link>
                <i className="ri-arrow-right-s-line text-white/30"></i>
                <span className="text-white/40 truncate max-w-xs">{article.title}</span>
              </nav>

              {/* Categoria */}
              <span className={`inline-block px-4 py-1.5 bg-gradient-to-r ${article.categoryColor} text-white text-sm font-['Montserrat'] font-semibold rounded-full mb-4`}>
                {article.category}
              </span>

              {/* Título */}
              <h1 className="font-['Playfair_Display'] text-3xl md:text-5xl text-white leading-tight mb-4">
                {article.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-6 text-white/70">
                <div className="flex items-center gap-3">
                  <img
                    src={article.author.avatar}
                    alt={article.author.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-gold-400"
                  />
                  <div>
                    <p className="font-['Montserrat'] font-semibold text-white text-sm">{article.author.name}</p>
                    <p className="font-['Montserrat'] text-xs text-white/50">{formattedDate}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 font-['Montserrat']">
                  <i className="ri-time-line text-gold-400/70"></i>
                  <span className="text-sm whitespace-nowrap">{article.readTime} de leitura</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conteúdo principal */}
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_280px] gap-12">

            {/* Artigo */}
            <article>
              {/* Resumo destacado */}
              <div className="bg-cream-100 border-l-4 border-gold-500 rounded-r-xl p-6 mb-10">
                <p className="font-['Playfair_Display'] text-lg text-charcoal-700 leading-relaxed italic">
                  {article.excerpt}
                </p>
              </div>

              {/* Conteúdo */}
              <div className="prose prose-lg max-w-none">
                {paragraphs.map((paragraph, index) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h2 key={index} className="font-['Playfair_Display'] text-2xl text-charcoal-800 mt-10 mb-4">
                        {paragraph.replace(/\*\*/g, '')}
                      </h2>
                    );
                  }
                  if (paragraph.includes('**')) {
                    const parts = paragraph.split(/\*\*(.*?)\*\*/g);
                    return (
                      <p key={index} className="font-['Montserrat'] text-charcoal-600 leading-relaxed mb-6 text-base font-light">
                        {parts.map((part, i) =>
                          i % 2 === 1 ? (
                            <strong key={i} className="font-semibold text-charcoal-800">{part}</strong>
                          ) : (
                            part
                          )
                        )}
                      </p>
                    );
                  }
                  if (/^\d+\./.test(paragraph)) {
                    const lines = paragraph.split('\n').filter(Boolean);
                    return (
                      <ol key={index} className="list-decimal list-inside space-y-3 mb-6 pl-2">
                        {lines.map((line, i) => {
                          const text = line.replace(/^\d+\.\s*/, '');
                          const parts = text.split(/\*\*(.*?)\*\*/g);
                          return (
                            <li key={i} className="font-['Montserrat'] text-charcoal-600 text-base leading-relaxed font-light">
                              {parts.map((part, j) =>
                                j % 2 === 1 ? (
                                  <strong key={j} className="font-semibold text-charcoal-800">{part}</strong>
                                ) : (
                                  part
                                )
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    );
                  }
                  if (paragraph.startsWith('-')) {
                    const lines = paragraph.split('\n').filter(Boolean);
                    return (
                      <ul key={index} className="space-y-3 mb-6 pl-2">
                        {lines.map((line, i) => {
                          const text = line.replace(/^-\s*/, '');
                          const parts = text.split(/\*\*(.*?)\*\*/g);
                          return (
                            <li key={i} className="flex items-start gap-3 text-charcoal-600 text-base leading-relaxed font-['Montserrat'] font-light">
                              <span className="w-2 h-2 rounded-full bg-gold-500 mt-2.5 shrink-0"></span>
                              <span>
                                {parts.map((part, j) =>
                                  j % 2 === 1 ? (
                                    <strong key={j} className="font-semibold text-charcoal-800">{part}</strong>
                                  ) : (
                                    part
                                  )
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    );
                  }
                  return (
                    <p key={index} className="font-['Montserrat'] text-charcoal-600 leading-relaxed mb-6 text-base font-light">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-10 pt-8 border-t border-gold-100/60">
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 bg-cream-100 text-charcoal-500 text-sm rounded-full hover:bg-gold-50 hover:text-gold-600 transition-colors cursor-pointer font-['Montserrat'] border border-gold-200/50"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Autor */}
              <div className="mt-10 p-8 bg-cream-100 rounded-2xl border border-gold-200">
                <div className="flex items-start gap-5">
                  <img
                    src={article.author.avatar}
                    alt={article.author.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-gold-400 shrink-0"
                  />
                  <div>
                    <p className="font-['Montserrat'] text-xs font-semibold text-gold-600 uppercase tracking-wider mb-1">Sobre a autora</p>
                    <h3 className="font-['Playfair_Display'] text-xl text-charcoal-800 mb-2">{article.author.name}</h3>
                    <p className="font-['Montserrat'] text-charcoal-500 leading-relaxed text-sm font-light">
                      Especialista em PNL, desenvolvimento pessoal e transformação de vida. Criadora do método Carta do Eu Futuro, que já transformou mais de 10.000 vidas ao redor do Brasil.
                    </p>
                    <Link
                      to="/sobre"
                      className="inline-flex items-center gap-2 mt-4 text-gold-600 font-['Montserrat'] font-semibold hover:text-gold-700 transition-colors text-sm"
                    >
                      Conhecer mais sobre Bia Holmes
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Navegação entre artigos */}
              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  onClick={() => navigate('/blog')}
                  className="inline-flex items-center gap-2 px-5 py-3 border border-gold-200 text-charcoal-600 font-['Montserrat'] font-medium rounded-xl hover:bg-cream-100 hover:border-gold-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar ao Blog
                </button>
                <Link
                  to="/planos"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-white font-['Montserrat'] font-semibold rounded-xl hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  Começar minha jornada
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-8">
              {/* CTA */}
              <div className="bg-gradient-to-br from-charcoal-900 to-charcoal-800 border border-gold-500/20 rounded-2xl p-6 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-gold-500/15 border border-gold-500/25 rounded-xl mb-4">
                  <i className="ri-quill-pen-line text-2xl text-gold-400"></i>
                </div>
                <h3 className="font-['Playfair_Display'] text-lg text-white mb-2">Escreva sua Carta</h3>
                <p className="font-['Montserrat'] text-white/55 text-sm leading-relaxed mb-5 font-light">
                  Comece hoje sua jornada de transformação com o método Carta do Eu Futuro.
                </p>
                <Link
                  to="/planos"
                  className="block text-center px-4 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-white font-['Montserrat'] font-semibold rounded-xl hover:from-gold-600 hover:to-gold-800 transition-all text-sm whitespace-nowrap cursor-pointer tracking-wide"
                >
                  Começar agora
                </Link>
              </div>

              {/* Artigos relacionados */}
              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="font-['Playfair_Display'] text-lg text-charcoal-800 mb-4 flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-article-line text-gold-500"></i>
                    </div>
                    Artigos relacionados
                  </h3>
                  <div className="space-y-3">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/blog/${related.slug}`}
                        className="group flex gap-3 p-3 rounded-xl hover:bg-cream-100 border border-transparent hover:border-gold-200/60 transition-all cursor-pointer"
                      >
                        <div className="w-20 h-16 rounded-lg overflow-hidden shrink-0">
                          <img
                            src={related.coverImage}
                            alt={related.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-['Montserrat'] text-sm font-semibold text-charcoal-700 group-hover:text-gold-600 transition-colors line-clamp-2 leading-snug">
                            {related.title}
                          </p>
                          <p className="font-['Montserrat'] text-xs text-charcoal-400 mt-1 whitespace-nowrap">{related.readTime} de leitura</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Todos os artigos */}
              <div>
                <h3 className="font-['Playfair_Display'] text-lg text-charcoal-800 mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-layout-grid-line text-gold-500"></i>
                  </div>
                  Mais artigos
                </h3>
                <div className="space-y-3">
                  {blogArticles
                    .filter((a) => a.slug !== slug)
                    .slice(0, 4)
                    .map((a) => (
                      <Link
                        key={a.id}
                        to={`/blog/${a.slug}`}
                        className="flex items-center gap-2 text-sm text-charcoal-500 hover:text-gold-600 transition-colors group cursor-pointer font-['Montserrat']"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-gold-400 group-hover:translate-x-1 transition-transform"></i>
                        </div>
                        <span className="line-clamp-1">{a.title}</span>
                      </Link>
                    ))}
                </div>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-gold-600 font-['Montserrat'] font-semibold hover:text-gold-700 transition-colors"
                >
                  Ver todos os artigos
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
